import { defineStore } from 'pinia'
import type { _AxiosError } from './api'
import axios from './api'
import { useUserStore } from './userStore'
import { _ElMessage } from '@/utils/element-plus-wrapper'
import type { UserWithLocalCredential } from '@/types/user.type'
import type { RouteAccess } from '@/router'
import { Constants } from '@/utils/enums'

// セッションからは使ったログイン方法が分からないので、端末に覚えておく
type LoginMethod = 'local' | 'weConnect'
const LOGIN_METHOD_STORAGE_KEY = 'loginMethod'

function saveLoginMethod(method: LoginMethod) {
  try {
    localStorage.setItem(LOGIN_METHOD_STORAGE_KEY, method)
  } catch (e) {
    console.error('Failed to save login method to localStorage', e)
  }
}

export function loadLoginMethod(): LoginMethod | null {
  try {
    const method = localStorage.getItem(LOGIN_METHOD_STORAGE_KEY)
    return method === 'local' || method === 'weConnect' ? method : null
  } catch (e) {
    console.error('Failed to load login method from localStorage', e)
    return null
  }
}

export const useAuthStore = defineStore('authStore', {
  state: () => ({}),
  getters: {
    is_loggedin(): boolean {
      const userStore = useUserStore()
      return !!userStore.currentUser
    },
    can_access_all_branches(): boolean {
      return !!useUserStore().currentUser?.canAccessAllBranches
    },
    is_system_admin(): boolean {
      const userStore = useUserStore()
      return !!userStore.currentUser?.isSystemAdmin
    },
    is_manager(): boolean {
      const userStore = useUserStore()
      return !!userStore.currentUser?.isManager
    },
    can_access(): (access: RouteAccess | undefined) => boolean {
      return (access) => {
        if (access === 'systemAdmin') return this.is_system_admin
        if (access === 'allBranches') return this.can_access_all_branches
        return true
      }
    },
  },
  actions: {
    authedHello() {
      return new Promise<UserWithLocalCredential>((resolve, reject) => {
        axios
          .get('/auth/hello')
          .then((r: { data: { user: UserWithLocalCredential } }) => {
            const userStore = useUserStore()
            userStore.setCurrentUser(r.data.user)
            resolve(r.data.user)
          })
          .catch((err: _AxiosError) => {
            console.log(err)
            reject(err)
          })
      })
    },
    login(json: { user_account_id: string; password: string }) {
      return new Promise<UserWithLocalCredential>((resolve, reject) => {
        axios
          .post('/auth/login', json)
          .then((res: { data: { user: UserWithLocalCredential } }) => {
            saveLoginMethod('weConnect')
            const userStore = useUserStore()
            userStore.setCurrentUser(res.data.user)
            resolve(res.data.user)
          })
          .catch((err: _AxiosError) => {
            console.log(err)
            _ElMessage({ type: 'error', message: 'Login failed' })
            reject(err)
          })
      })
    },
    // Entra ID SSO: API の認可エンドポイントへページ遷移する。
    entraLogin() {
      saveLoginMethod('weConnect')
      window.location.href = `${Constants.BASE_URL_API}/auth/entra-id/login`
    },
    localLogin(json: { email: string; password: string }) {
      return new Promise<UserWithLocalCredential>((resolve, reject) => {
        axios
          .post('/auth/local/login', json)
          .then((res: { data: { user: UserWithLocalCredential } }) => {
            saveLoginMethod('local')
            const userStore = useUserStore()
            userStore.setCurrentUser(res.data.user)
            resolve(res.data.user)
          })
          .catch((err: _AxiosError) => {
            _ElMessage({ type: 'error', message: 'Login failed' })
            console.log(err)
            reject(err)
          })
      })
    },
    logout() {
      return new Promise<void>((resolve, reject) => {
        axios
          .post('/auth/logout')
          .then(() => {
            const userStore = useUserStore()
            userStore.clearCurrentUser()
            resolve()
          })
          .catch((err: _AxiosError) => {
            console.error(err)
            reject(err)
          })
      })
    },
  },
})
